/* eslint-disable react/jsx-no-useless-fragment */
import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { useField } from 'formik';
import { ThemeProvider } from 'styled-components';
import { IsoDateType, Checkbox, InfoBox, utmostTheme, tokens } from '@utmost.co/utmost-design-system';
import { getElementFields } from '../../forms/utils/getElementFields';
import { SmartformElementData } from '../../types';
import { TextFieldAtom } from '../../atoms/TextFieldAtom/TextFieldAtom';
import { useSmartFormContext } from '../../forms/utils/useSmartFormContext';
import { nationalIdUtil } from '../../forms/utils/validation/nationalIdUtil';
import MultiColumns from '../../forms/layouts/MultiColumns';
import { DatePicker } from '../../fields/DatePicker';
import { objectUtil } from '../../forms/utils';
import { filterNotEmpty } from '../../forms/utils/listHelpers';
import { InfoText } from '../../atoms/InfoText/InfoText';
import { primaryNationalIdentificationMetaName as metaName } from './primaryNationalIdentification.helpers';

export type IDValue = { code?: string; value?: string; validUntil?: string };

interface ExposedProps {
  valueName?: string;
  validUntilName?: string;
  disabled?: boolean;
  onChange?(value?: IDValue): void;
}

export const errorValue = (error: any | string, key: string) => {
  if (!error) return undefined;

  if (typeof error === 'string') return error;

  return objectUtil.getIn(error, key);
};

export const PrimaryNationalIdentification: React.FC<SmartformElementData & ExposedProps> = (props) => {
  const element = props.element;
  const {
    required: [ssnValue, hasNoSsn],
    optional: { confirmSsn }
  } = getElementFields<2>(props);
  const { setFieldValue, values, setFieldValues } = useSmartFormContext();
  const [confirm, setConfirm] = useState(false);

  const { currentMask, hasValidUntilDate } = nationalIdUtil.setup(
    props.element.type, // this changing to props.element.type to do the mask
    element.params?.id ?? '',
    element.payload?.nationalIdTypes ?? []
  );

  const valueName = props.valueName ?? `${ssnValue.name}.value`;
  const validUntilName = props.validUntilName ?? `${ssnValue.name}.validUntil`;
  const code = element.params?.id;
  const hasErrors = !!ssnValue.error;

  const currentValue = useMemo(
    () => ({
      value: objectUtil.getIn(values, valueName),
      validUntil: objectUtil.getIn(values, validUntilName)
    }),
    [values, validUntilName, valueName]
  );

  const isValidUntilRequired = hasValidUntilDate && (!!currentValue.value || ssnValue.validation.required);

  const [ssnValueProps, ssnMeta] = useField<IDValue | string>(valueName);
  const [confirmProps] = useField<any>(confirmSsn?.name || 'INVALID');

  useEffect(() => {
    setFieldValue(metaName.validUntilRequired, isValidUntilRequired);
  }, [isValidUntilRequired, setFieldValue]);

  useEffect(() => {
    setFieldValue(metaName.valueRequired, ssnValue.validation.required ?? false);
  }, [ssnValue.validation.required, setFieldValue]);

  useEffect(() => {
    setFieldValue(hasNoSsn.name, !hasErrors);
  }, [hasErrors, setFieldValue, hasNoSsn.name]);

  const changeCodeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const output = {
        ...currentValue,
        code: value ? code : undefined,
        value: value || undefined
      };

      if (props.onChange) {
        props.onChange(output);
      } else {
        setFieldValue(ssnValue.name, output);
      }
    },
    [setFieldValue, props, currentValue, code, ssnValue?.name]
  );

  const changeUntilValidHandler = useCallback(
    (value: IsoDateType) => {
      const output = {
        ...currentValue,
        code: currentValue.value ? code : undefined,
        validUntil: value
      };
      if (props.onChange) {
        props.onChange(output);
      } else {
        setFieldValue(ssnValue.name, output);
      }
    },
    [setFieldValue, props, code, currentValue, ssnValue?.name]
  );

  const resetValues = () => {
    if (props.onChange) {
      props.onChange({ code: undefined, validUntil: undefined, value: undefined });
      setFieldValues({
        [hasNoSsn?.name]: true,
        [metaName.valueRequired]: false,
        [metaName.validUntilRequired]: false,
        [metaName.confirmSsn]: undefined
      });
    } else {
      setFieldValues({
        [hasNoSsn?.name]: true,
        [ssnValue.name]: undefined,
        [metaName.valueRequired]: false,
        [metaName.validUntilRequired]: false,
        [metaName.confirmSsn]: undefined
      });
    }
  };

  let value = '';

  if (ssnValueProps.value)
    if (typeof ssnValueProps.value === 'string') value = ssnValueProps.value;
    else value = ssnValueProps.value.value || '';

  if (!code) {
    return <>Set NationalId Type in params.id</>;
  }
  return (
    <>
      <MultiColumns
        xs={[12, 12, 12]}
        md={[hasValidUntilDate ? 8 : 12, confirmSsn ? 12 : 4, 12]}
        elements={filterNotEmpty([
          <TextFieldAtom
            key={ssnValue.name}
            name={valueName}
            helperText={ssnMeta.error || ssnValue.helpText}
            infoText={ssnValue.infoText}
            label={ssnValue.label}
            type={element.type}
            placeholder={ssnValue.hint ? ssnValue.hint : (currentMask ?? '')}
            error={(props.submitted || ssnValue.touched) && !!ssnMeta.error}
            required={ssnValue.validation.required || !!currentValue.validUntil}
            fullWidth
            disabled={confirm || props.disabled}
            mask={currentMask}
            onChange={changeCodeHandler}
            value={value}
          />,
          hasValidUntilDate ? (
            <DatePicker
              disablePast
              disabled={confirm || props.disabled}
              name={validUntilName}
              key={validUntilName}
              label="Valid Until"
              required={isValidUntilRequired}
              dateFormat={element.params?.dateFormat}
              onChange={changeUntilValidHandler as any}
            />
          ) : undefined,
          confirmSsn ? (
            <TextFieldAtom
              name={confirmSsn.name}
              helperText={errorValue(confirmSsn.error, 'value') || confirmSsn.helpText}
              infoText={confirmSsn.infoText}
              label={confirmSsn.label}
              type={element.type}
              placeholder={confirmSsn.hint ? confirmSsn.hint : (currentMask ?? '')}
              error={props.submitted && !!errorValue(confirmSsn.error, 'value')}
              required={ssnValue.validation.required || !!currentValue.value}
              fullWidth
              disabled={confirm}
              key={confirmSsn.name}
              mask={currentMask}
              value={confirmProps.value || ''}
              onChange={(e) => setFieldValue(confirmSsn.name, e.target.value)}
            />
          ) : undefined
        ])}
      />
      {hasNoSsn?.validation?.required && (
        <div style={{ margin: '0.5rem 0 1rem 0', display: 'flex' }}>
          <Checkbox
            id="primary-nat-id-confirm"
            checked={confirm}
            onChange={() => {
              if (!confirm) {
                resetValues();
              } else {
                setFieldValues({
                  [hasNoSsn?.name]: undefined,
                  [metaName.valueRequired]: ssnValue.validation.required ?? false,
                  [metaName.validUntilRequired]: isValidUntilRequired ?? false
                });
              }
              setConfirm(!confirm);
            }}
          >
            <InfoText
              label={element.params?.noSSNLabel || "I don't have an SSN National Identification to add"}
              required
              tooltipText={hasNoSsn.infoText}
              top={0}
            />
          </Checkbox>
        </div>
      )}
      {(!!hasNoSsn?.error || !!ssnValue.error) && props.submitted && !!hasNoSsn.validation.required && (
        <ThemeProvider theme={{ ...utmostTheme, ...tokens }}>
          <InfoBox title={hasNoSsn?.label} type="ALERT">
            {hasNoSsn?.helpText}
          </InfoBox>
        </ThemeProvider>
      )}
    </>
  );
};
